import React from 'react';
import { Phone, Mail, Instagram, Facebook, Linkedin } from 'lucide-react';
import { useTranslation } from '../translations';

interface FooterConfig {
  phone?: string;
  email?: string;
  address?: string;
  vatNumber?: string;
  socialLinks?: {
    instagram?: string;
    facebook?: string;
    linkedin?: string;
  };
  links?: {
    terms?: string;
    privacy?: string;
  };
}

const defaultConfig: FooterConfig = {
  phone: '+32 460 20 25 23',
  email: 'bizcomfort@jaimy.be',
  address: 'Karel Rogierplein 11, 1210 Brussel',
  vatNumber: 'BE0699.693.365',
  socialLinks: {
    instagram: 'https://www.instagram.com/jaimy.be/',
    facebook: 'https://www.facebook.com/jaimy.be',
    linkedin: 'https://www.linkedin.com/company/18832232/'
  },
  links: {
    terms: 'https://portal.jaimy.be/terms-conditions',
    privacy: 'https://portal.jaimy.be/legal'
  }
};

export function Footer({ config = defaultConfig }: { config?: Partial<FooterConfig> }) {
  const { t } = useTranslation();
  const mergedConfig = { ...defaultConfig, ...config };

  return (
    <footer className="bg-gradient-to-br from-[#3C5679] to-[#2A3F5F] text-white py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
          {/* Contact Section - Left aligned */}
          <div className="text-left">
            <div className="mb-4">
              <h3 className="font-medium mb-2">{t.footer.phone}</h3>
              <a 
                href={`tel:${mergedConfig.phone}`}
                className="text-white hover:opacity-80 transition-opacity underline"
              >
                {mergedConfig.phone}
              </a>
            </div>
            <div>
              <h3 className="font-medium mb-2">{t.footer.email}</h3>
              <a 
                href={`mailto:${mergedConfig.email}`}
                className="text-white hover:opacity-80 transition-opacity underline"
              >
                {mergedConfig.email}
              </a>
            </div>
          </div>

          {/* Products Section - Center aligned */}
          <div className="text-center">
            <h3 className="font-medium mb-4">{t.footer.products}</h3>
            <div className="space-y-2">
              <div>
                <a 
                  href="https://jaimy.be/nl/"
                  className="text-white hover:opacity-80 transition-opacity"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Jaimy voor huiseigenaren
                </a>
              </div>
              <div>
                <a 
                  href="https://pro.jaimy.be/nl"
                  className="text-white hover:opacity-80 transition-opacity"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Pro Platform
                </a>
              </div>
              <div>
                <a 
                  href="https://homecomfort.be"
                  className="text-white hover:opacity-80 transition-opacity"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Homecomfort
                </a>
              </div>
              <div>
                <a 
                  href="https://www.ajusto.be/nl"
                  className="text-white hover:opacity-80 transition-opacity"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Ajusto
                </a>
              </div>
            </div>
          </div>

          {/* Social Section - Right aligned */}
          <div className="text-right">
            <h3 className="font-medium mb-2">{t.footer.follow}</h3>
            <div className="flex gap-4 justify-end">
              {mergedConfig.socialLinks?.instagram && (
                <a 
                  href={mergedConfig.socialLinks.instagram}
                  className="text-white hover:opacity-80 transition-opacity"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Instagram className="h-6 w-6" />
                </a>
              )}
              {mergedConfig.socialLinks?.facebook && (
                <a 
                  href={mergedConfig.socialLinks.facebook}
                  className="text-white hover:opacity-80 transition-opacity"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Facebook className="h-6 w-6" />
                </a>
              )}
              {mergedConfig.socialLinks?.linkedin && (
                <a 
                  href={mergedConfig.socialLinks.linkedin}
                  className="text-white hover:opacity-80 transition-opacity"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Linkedin className="h-6 w-6" />
                </a>
              )}
            </div>
          </div>
        </div>

        {/* Copyright Section */}
        <div className="border-t border-white/10 pt-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <p className="text-white text-sm">
              Copyright {new Date().getFullYear()}© Jaimy by Belfius, {mergedConfig.address} - {mergedConfig.vatNumber}
            </p>
            <div className="flex items-center gap-4 text-sm">
              {mergedConfig.links?.terms && (
                <a 
                  href={mergedConfig.links.terms}
                  className="text-white hover:opacity-80 transition-opacity"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t.footer.terms}
                </a>
              )}
              <span className="text-white">•</span>
              {mergedConfig.links?.privacy && (
                <a 
                  href={mergedConfig.links.privacy}
                  className="text-white hover:opacity-80 transition-opacity"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t.footer.privacy}
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}